import React, { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Building2, Package, Plus, Trash2, ChevronDown, Check, Search, AlertTriangle } from 'lucide-react'

// Percorso relativo (stessa origine): vedi commento in useAttachmentPreview.js
const API_BASE = ''
// Unità di misura selezionabili per riga — stesse chiavi CANONICHE di
// SINONIMI_UM in jenia-queuer/src/queuer/measure.py (mai le varianti/sinonimi
// grezzi come "PZ"/"N.": quelle il Queuer le normalizza già in NR prima che
// arrivino qui, vedi backend-consumer.js:buildFlatOrderShape). "MQ" è stato
// rimosso: non esiste né nel dizionario dei sinonimi né in nessun record
// ar_unmis reale verificato a DB — non era un'unità di misura valida.
const UM_OPTIONS = ['NR', 'KG', 'MT', 'LT', 'CF', 'SC']

// Le uniche due opzioni per il tipo di documento — l'LLM prova a classificare
// il documento in ingresso, ma il commerciale può sempre correggerlo a mano.
const ORDER_TYPE_OPTIONS = ['Ordine', 'Offerta']

function toDateInputValue(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  return d.toISOString().slice(0, 10)
}

// Indirizzo: default al best match individuato dall'LLM (job.destination,
// es. un cantiere o uno showroom) se presente, altrimenti alla sede legale
// del cliente (job.client_legal_address) — mai vuoto se sappiamo almeno
// dove ha sede il cliente.
function computeDefaultDestination(job) {
  return job?.destination || job?.client_legal_address || ''
}

// Default tipo documento: quello rilevato dall'LLM (job.order_type,
// 'ordine'|'offerta') se presente, altrimenti "Ordine".
function computeDefaultOrderType(job) {
  const detected = (job?.order_type || '').toString().trim().toLowerCase()
  return detected === 'offerta' ? 'Offerta' : 'Ordine'
}

// Hook condiviso da tutte le tendine custom del componente. Il menu viene
// calcolato in coordinate di viewport (position: 'fixed') e renderizzato
// via portal dentro document.body, invece che come figlio con
// position:'absolute' del bottone. Motivo: "Righe ordine" scorre al suo
// interno (s.linesTable ha overflow:'auto') — un menu absolute annidato lì
// dentro veniva tagliato di netto dal bordo del contenitore scrollabile
// non appena si apriva vicino al fondo (bug segnalato: tendina UM/
// Descrizione troncata a metà). Con fixed + portal il menu non dipende più
// da nessun antenato con overflow/scroll, quindi non viene mai clippato.
// `width`, se passato, sgancia la larghezza del menu da quella del bottone
// che lo apre (serve ai menu a due colonne, molto più larghi del campo).
// In quel caso il menu resta comunque ancorato al bordo sinistro del
// bottone, ma se sforerebbe il bordo destro della finestra viene spostato a
// sinistra quel tanto che basta per restare visibile.
function useAnchoredMenu({ width } = {}) {
  const [open, setOpen] = useState(false)
  const anchorRef = useRef(null)
  const menuRef = useRef(null)
  const [menuStyle, setMenuStyle] = useState(null)

  useEffect(() => {
    if (!open) return

    const GAP = 4 // stacco dal trigger, invariato
    const VIEWPORT_MARGIN = 8 // margine minimo dal bordo della finestra

    // Se il trigger è vicino al fondo della pagina (es. l'ultima riga
    // ordine), un menu che si apre sempre verso il basso finisce oltre
    // window.innerHeight — essendo position:fixed, quella parte non è mai
    // raggiungibile scrollando la pagina (bug segnalato: tendina Articolo
    // troncata sull'ultima riga). Calcoliamo quindi lo spazio realmente
    // disponibile sopra e sotto il trigger e: 1) apriamo il menu verso
    // l'alto quando sotto non c'è abbastanza posto ma sopra ce n'è di più,
    // 2) in ogni caso limitiamo maxHeight allo spazio disponibile nella
    // direzione scelta, cosa che prima non veniva mai fatta (i vari
    // maxHeight nei singoli stili — 220/420 — erano valori fissi, non
    // adattati alla posizione reale del trigger).
    const updatePosition = () => {
      const rect = anchorRef.current?.getBoundingClientRect()
      if (!rect) return
      const menuWidth = width ? Math.min(width, window.innerWidth - 16) : rect.width
      const left = width
        ? Math.max(8, Math.min(rect.left, window.innerWidth - menuWidth - 8))
        : rect.left

      const spaceBelow = window.innerHeight - rect.bottom - GAP - VIEWPORT_MARGIN
      const spaceAbove = rect.top - GAP - VIEWPORT_MARGIN
      const openUp = spaceBelow < 200 && spaceAbove > spaceBelow

      setMenuStyle({
        position: 'fixed',
        left,
        width: menuWidth,
        right: 'auto',
        zIndex: 200,
        ...(openUp
          ? { top: 'auto', bottom: window.innerHeight - rect.top + GAP, maxHeight: Math.max(120, spaceAbove) }
          : { top: rect.bottom + GAP, bottom: 'auto', maxHeight: Math.max(120, spaceBelow) }),
      })
    }
    updatePosition()

    const handleClickOutside = (e) => {
      if (anchorRef.current?.contains(e.target)) return
      if (menuRef.current?.contains(e.target)) return
      setOpen(false)
    }

    // capture:true per intercettare anche lo scroll di contenitori interni
    // (es. s.linesTable), non solo quello della finestra.
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open])

  return { open, setOpen, anchorRef, menuRef, menuStyle }
}

// Tendina custom per la descrizione di ogni riga ordine — stesso stile
// della Ragione sociale: in alto i best match, sotto la ricerca su tutta
// l'anagrafica. Se il Matcher non ha restituito alternative ma la riga ha
// comunque una descrizione estratta (LLM/allegato), quella descrizione
// diventa l'unico "best match" — così la tendina resta sempre disponibile.
// Sempre la tendina di ricerca, mai testo libero digitato a mano — anche
// per una riga vuota aggiunta con "+ Aggiungi riga" (bestMatches vuoto in
// quel caso, SearchableMatchSelect lo gestisce già mostrando solo
// `emptyLabel` finché non si cerca): un articolo scritto a mano non
// corrisponderebbe a nessun codice reale di catalogo, va sempre cercato nel
// database (richiesta esplicita 2026-08-11). L'onChange verso il chiamante
// riceve sempre un oggetto {name, code?} così selectLineDescription (più
// sotto) può sempre leggere .name/.code senza distinguere i casi.
//
// `invalid` (passato dal chiamante, vedi il loop delle righe più sotto):
// bordo rosso quando la riga non ha un articolo matchato (`matched_article`
// vuoto) — stesso "nessun match trovato" della Ragione sociale quando il
// cliente non ha un codice gestionale associato (vedi `clientCode` più
// sotto). Non si basa sulla sola assenza di `value` (la descrizione può
// benissimo esserci, estratta dall'LLM, pur senza un articolo di catalogo
// collegato — è proprio quel caso a richiedere più attenzione da parte del
// commerciale).
function LineDescriptionSelect({ value, options, onChange, invalid = false, disabled = false }) {
  const matches = options && options.length > 0 ? options : (value ? [{ name: value }] : [])

  return (
    <div style={s.lineDescWrap}>
      <SearchableMatchSelect
        value={value}
        bestMatches={matches}
        searchEndpoint="/api/articles/search"
        onChange={(name, match) => onChange(match || { name })}
        emptyLabel="Descrizione non disponibile"
        searchPlaceholder="Cerca descrizione articolo..."
        triggerStyle={s.lineDescSelectLike}
        triggerTextStyle={s.lineDescSelectText}
        invalid={invalid}
        disabled={disabled}
      />
    </div>
  )
}

// Tendina custom per la UM di ogni riga ordine — stessa interazione della
// tendina "Ragione sociale" (bottone + menu assoluto + click-outside), ma
// senza percentuale di confidenza: qui le opzioni sono un elenco fisso.
function UmSelect({ value, onChange, disabled = false }) {
  const { open, setOpen, anchorRef, menuRef, menuStyle } = useAnchoredMenu()

  return (
    <div style={{ position: 'relative' }} ref={anchorRef}>
      <button
        type="button"
        disabled={disabled}
        style={{ ...s.umSelectLike, ...(disabled ? s.disabledField : {}) }}
        onClick={() => setOpen((v) => !v)}
      >
        <span style={s.umSelectText}>{value || 'NR'}</span>
        <ChevronDown size={13} color="var(--text-3)" />
      </button>

      {open && menuStyle && createPortal(
        <div ref={menuRef} style={{ ...s.umDropdown, ...menuStyle }}>
          {UM_OPTIONS.map((opt) => (
            <button
              type="button"
              key={opt}
              style={s.umDropdownItem}
              onClick={() => { onChange(opt); setOpen(false) }}
            >
              <span style={s.clientDropdownCheck}>
                {opt === value && <Check size={13} />}
              </span>
              <span style={s.umDropdownLabel}>{opt}</span>
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  )
}

// Tendina custom generica per campi a scelta singola su una lista di
// stringhe — stesso stile/interazione della tendina "Ragione sociale"
// (bottone finto-select + menu assoluto + click-outside). Usata per
// Destinazione alternativa (sedi note) e Tipo documento (Ordine/Offerta).
// allowClear aggiunge in cima un'opzione per svuotare il campo — usata solo
// dove il vuoto è un valore valido (Destinazione alternativa, appunto).
function LabeledSelect({ value, options, onChange, emptyLabel, allowClear = false, disabled = false }) {
  const { open, setOpen, anchorRef, menuRef, menuStyle } = useAnchoredMenu()

  return (
    <div style={{ position: 'relative' }} ref={anchorRef}>
      <button
        type="button"
        disabled={disabled}
        style={{ ...s.selectLike, ...(disabled ? s.disabledField : {}) }}
        onClick={() => setOpen((v) => !v)}
      >
        <span style={s.selectText}>{value || emptyLabel}</span>
        <ChevronDown size={14} color="var(--text-3)" />
      </button>

      {open && menuStyle && createPortal(
        <div ref={menuRef} style={{ ...s.clientDropdown, ...menuStyle }}>
          {allowClear && (
            <button
              type="button"
              style={s.clientDropdownItem}
              onClick={() => { onChange(''); setOpen(false) }}
            >
              <span style={s.clientDropdownCheck}>
                {!value && <Check size={13} />}
              </span>
              <span style={{ ...s.clientDropdownName, color: 'var(--text-3)', fontStyle: 'italic' }}>{emptyLabel}</span>
            </button>
          )}
          {options.length === 0 && !allowClear && (
            <div style={s.clientDropdownEmpty}>Nessuna opzione disponibile</div>
          )}
          {options.map((opt) => (
            <button
              type="button"
              key={opt}
              style={s.clientDropdownItem}
              onClick={() => { onChange(opt); setOpen(false) }}
            >
              <span style={s.clientDropdownCheck}>
                {opt === value && <Check size={13} />}
              </span>
              <span style={s.clientDropdownName}>{opt}</span>
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  )
}

// Larghezza fissa del menu — allargata (420 -&gt; 560, richiesta esplicita
// 2026-08-14: testi ancora tagliati) e comunque non decisiva da sola: il
// testo va sempre a capo se serve (vedi clientDropdownName), quindi qui
// serve solo a limitare quanto spesso capita, non a garantirlo.
// (SEARCH_MENU_WIDTH rimossa 2026-08-14: la larghezza fissa restava comunque
// piu' stretta della riga che apre il menu, es. Articolo — ora il menu
// prende semplicemente la larghezza vera del campo, vedi useAnchoredMenu()
// senza `width` in SearchableMatchSelect sotto.)

// Tendina a colonna singola per campi con "best match" del Matcher/LLM: in
// alto i 3-5 candidati migliori (stesso comportamento di sempre), sotto una
// barra di ricerca su tutta l'anagrafica per i casi in cui nessuno dei best
// match è quello giusto. La ricerca NON è live: parte solo al click sulla
// lente o premendo Invio, non a ogni carattere digitato — il futuro
// endpoint reale non supporterà query automatiche a ogni tasto (vedi
// CLAUDE.md). Oggi il "risultato" è un filtro locale sul mock
// mockDirectories.js, ma l'interazione è già quella pensata per una vera
// chiamata di rete (un solo giro per ricerca). allowClear aggiunge in cima
// un'opzione per svuotare il campo.
//
// `bestMatches` accetta sia stringhe semplici (es. destinazioni) sia oggetti
// {name, ...} (es. {name, code} per cliente/articolo, dove code è il
// codice gestionale/articolo collegato allo stesso record — vedi
// selectClient/selectLineDescription). `onChange(name, match)` riceve
// sempre il nome selezionato, e in più l'oggetto match originale quando la
// scelta viene dai best match (utile per sincronizzare campi collegati come
// il codice); è `undefined` quando la scelta viene dalla ricerca sotto o da
// "svuota campo", perché lì non c'è nessun record collegato da sincronizzare.
// `valueLabel` (opzionale) sostituisce solo il testo principale mostrato
// nel trigger — `value` resta il nome "vero" usato per aprire/evidenziare la
// voce corrispondente in tendina. `valueSuffix` (opzionale) aggiunge un
// secondo valore staccato sulla destra del trigger, es. il codice
// gestionale accanto al nome cliente (nome a sinistra troncato con
// ellissi se lungo, codice sempre intero a destra — non concatenati in
// un'unica stringa). `invalid` (opzionale, default false): bordo rosso sul
// trigger per segnalare "nessun match trovato" — lasciato a discrezione del
// chiamante (non dedotto automaticamente da `!value`) perché per alcuni
// campi il vuoto è una scelta legittima e non un errore (es. Indirizzo, che
// ha `allowClear`), non un'assenza di match da segnalare.
function SearchableMatchSelect({
  value,
  valueLabel,
  valueSuffix,
  bestMatches,
  searchEndpoint,
  searchParams = {},
  onChange,
  emptyLabel,
  searchPlaceholder = 'Cerca...',
  allowClear = false,
  triggerStyle = s.selectLike,
  triggerTextStyle = s.selectText,
  invalid = false,
  disabled = false,
}) {
  const { open, setOpen, anchorRef, menuRef, menuStyle } = useAnchoredMenu()
  const [query, setQuery] = useState('')
  // null = ricerca non ancora lanciata in questa apertura (si vedono solo i
  // best match); array di {name, code} = risultato dell'ultima ricerca
  // eseguita (anche [] se zero risultati). Una ricerca per click/Invio, non
  // per carattere digitato — vedi runSearch.
  const [searchResults, setSearchResults] = useState(null)
  const [searching, setSearching] = useState(false)
  const [searchError, setSearchError] = useState(null)

  useEffect(() => {
    if (!open) {
      setQuery('')
      setSearchResults(null)
      setSearchError(null)
    }
  }, [open])

  const runSearch = async () => {
    const q = query.trim()
    if (q.length < 2) return
    setSearching(true)
    setSearchError(null)
    try {
      const qs = new URLSearchParams({ q, ...searchParams }).toString()
      const res = await fetch(`${API_BASE}${searchEndpoint}?${qs}`)
      const body = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(body?.error || `status ${res.status}`)
      setSearchResults(body.results || [])
    } catch (err) {
      setSearchResults([])
      setSearchError(err.message)
    } finally {
      setSearching(false)
    }
  }

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      runSearch()
    }
  }

  const normalizedBestMatches = bestMatches.map((m) => (typeof m === 'string' ? { name: m } : m))

  const selectMatch = (match) => {
    onChange(match.name, match)
    setOpen(false)
  }

  const selectSearchResult = (result) => {
    onChange(result.name, result)
    setOpen(false)
  }

  const clear = () => {
    onChange('', undefined)
    setOpen(false)
  }

  return (
    <div style={{ position: 'relative' }} ref={anchorRef}>
      <button
        type="button"
        disabled={disabled}
        style={{ ...triggerStyle, ...(invalid ? s.invalidField : {}), ...(disabled ? s.disabledField : {}) }}
        onClick={() => setOpen((v) => !v)}
      >
        <span style={{ ...triggerTextStyle, flex: 1, minWidth: 0, textAlign: 'left' }}>
          {value ? (valueLabel || value) : emptyLabel}
        </span>
        <span style={s.selectTrailing}>
          {value && valueSuffix && <span style={s.selectSuffix}>{valueSuffix}</span>}
          <ChevronDown size={14} color="var(--text-3)" style={{ flexShrink: 0 }} />
        </span>
      </button>

      {open && menuStyle && createPortal(
        <div ref={menuRef} style={{ ...s.searchSelectDropdown, ...menuStyle }}>
          <div style={s.searchSelectMatches}>
            <div style={s.splitColumnHeader}>Best match</div>
            {allowClear && (
              <button type="button" style={s.clientDropdownItem} onClick={clear}>
                <span style={s.clientDropdownCheck}>{!value && <Check size={13} />}</span>
                <span style={{ ...s.clientDropdownName, color: 'var(--text-3)', fontStyle: 'italic' }}>{emptyLabel}</span>
              </button>
            )}
            {normalizedBestMatches.length === 0 && (
              <div style={s.clientDropdownEmpty}>Nessun match trovato</div>
            )}
            {normalizedBestMatches.map((match) => (
              <button type="button" key={match.name} style={s.clientDropdownItem} onClick={() => selectMatch(match)}>
                <span style={s.clientDropdownCheck}>{match.name === value && <Check size={13} />}</span>
                <span style={s.clientDropdownName}>{match.name}</span>
                {match.code && <span style={s.selectSuffix}>{match.code}</span>}
              </button>
            ))}
          </div>

          <div style={s.searchSelectDivider} />

          <div style={s.searchSelectSearchRow}>
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder={searchPlaceholder}
              style={s.searchSelectInput}
            />
            <button
              type="button"
              style={s.searchSelectSearchBtn}
              onClick={runSearch}
              aria-label="Cerca"
              title="Cerca"
            >
              <Search size={14} />
            </button>
          </div>

          {searching && (
            <div style={s.clientDropdownEmpty}>Ricerca in corso...</div>
          )}
          {!searching && searchError && (
            <div style={s.clientDropdownEmpty}>Ricerca non riuscita: {searchError}</div>
          )}
          {!searching && !searchError && searchResults !== null && (
            <div style={s.searchResultsList}>
              {searchResults.length === 0 && (
                <div style={s.clientDropdownEmpty}>Nessun risultato per &ldquo;{query}&rdquo;</div>
              )}
              {searchResults.map((result) => (
                <button
                  type="button"
                  key={result.code || result.name}
                  style={s.clientDropdownItem}
                  onClick={() => selectSearchResult(result)}
                >
                  <span style={s.clientDropdownCheck}>{result.name === value && <Check size={13} />}</span>
                  <span style={s.clientDropdownName}>{result.name}</span>
                  {result.code && <span style={s.selectSuffix}>{result.code}</span>}
                </button>
              ))}
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  )
}

// Popup centrale (non più un banner in cima al pannello, su richiesta —
// un banner inline poteva passare inosservato se il commerciale stava
// guardando il documento a sinistra): overlay a schermo intero, card
// centrata. createPortal su document.body (stesso pattern già usato qui per
// i dropdown, vedi sopra) — altrimenti l'overlay resterebbe intrappolato
// dentro gli `overflow:hidden` del layout (PdfViewer/Dashboard), niente
// coprirebbe davvero l'intera finestra. onClick sul backdrop chiude come il
// bottone — click fuori = "ho visto", stessa azione di un dismiss esplicito.
function ErrorModal({ errors, onDismiss }) {
  if (!errors.length) return null
  return createPortal(
    <div style={s.errorModalOverlay} onClick={onDismiss}>
      <div style={s.errorModalCard} onClick={(e) => e.stopPropagation()}>
        {errors.map((err, i) => (
          <div key={i} style={s.errorModalRow}>
            <AlertTriangle size={20} style={{ flexShrink: 0, color: '#DC2626', marginTop: 1 }} />
            <span style={s.errorModalText}>{err.message}</span>
            {err.code != null && <span style={s.errorBannerCode}>{err.code}</span>}
          </div>
        ))}
        <button style={s.errorModalClose} onClick={onDismiss}>Ho capito</button>
      </div>
    </div>,
    document.body
  )
}

// Vista "campi estratti" per l'ordine in revisione. Layout: un'unica card
// "Recap. ordine" (Cliente, Indirizzo, Tipo documento), sotto la tabella
// Righe ordine.
//
// Tutti i campi qui sono modificabili a mano dal commerciale (codice
// gestionale, data, tipo documento, indirizzo, descrizioni riga, articolo
// matchato, quantità, U.M., consegna) — restano solo nello stato locale del
// componente finché non esiste un endpoint reale per salvarli (vedi TODO in
// Dashboard.jsx / handleApprove).
//
// Cliente, Indirizzo e Descrizione riga sono tendine con ricerca
// (SearchableMatchSelect): in alto i best match del Matcher/LLM, sotto una
// ricerca su tutta l'anagrafica (mock finché il backend non espone un
// endpoint di ricerca reale — vedi mockDirectories.js e CLAUDE.md), avviata
// solo al click sulla lente/Invio, non a ogni carattere.
//
// Data documento: campo tenuto nello stato ma non mostrato in UI su
// richiesta (recap ordine ridotto a 3 soli campi visibili) — non rimosso
// perché torna utile appena serve di nuovo in vista.
//
// `onLineHover`/`onLineActivate`/`onConfirmedLinesChange` (opzionali):
// collegamento col pannello documento a sinistra (PdfViewer), vedi
// Dashboard.jsx/Coda.jsx.
// - `onLineHover(line | null)`: mentre il puntatore è sopra una riga ordine,
//   il documento a sinistra cerchia/evidenzia (riquadro AZZURRO, temporaneo)
//   la porzione di testo corrispondente — nessuno scroll, solo un
//   riferimento visivo. Chiamato con `null` quando il puntatore esce dalla
//   riga: il riquadro azzurro sparisce del tutto, non resta su nessuna riga.
// - `onConfirmedLinesChange(string[])`: ad ogni cambio di `lines`, la lista
//   dei testi (raw_text/description) delle righe attualmente confermate —
//   il documento a sinistra ne disegna un riquadro VERDE fisso per
//   ciascuna, N in tutto, indipendente dall'hover (richiesta esplicita
//   2026-08-12: un riquadro verde per articolo confermato, permanente, più
//   quello azzurro che segue solo l'hover).
// - `onLineActivate(line)`: doppio click su una riga → il documento a
//   sinistra scorre fino a portare in cima quella porzione di testo. Non più
//   automatico durante lo scroll di "Righe ordine" (era così in un giro
//   precedente, tolto su richiesta): l'azione va innescata esplicitamente.
// In tutti i casi il matching è euristico (ricerca della sola descrizione
// come sottostringa nel testo grezzo, nessuna posizione salvata da parser/
// LLM/Matcher — vedi PdfViewer.jsx): può non trovare nulla o sbagliare con
// descrizioni duplicate o molto generiche.
//
// `systemErrors` (opzionale, array di {code, message}): errori di sistema
// da mostrare in un popup centrale (non più un banner inline, su richiesta —
// poteva passare inosservato) — caricamento PDF falito, chiamata
// Approva/Rifiuta fallita (sollevati da Dashboard.jsx/Coda.jsx, componenti
// fratelli, quindi passati come prop) più l'eventuale `job.system_error`
// (arriva già dentro il job stesso, non va sollevato). `code` è il codice
// errore dal backend quando disponibile — vedi ErrorModal più sopra.
export default function OrderReview({ job, onLineHover, onLineActivate, systemErrors = [], onOverridesChange, onConfirmedLinesChange }) {
  // Un ordine già approvato è definitivo (vedi Dashboard.jsx/Coda.jsx:
  // Concludi/Rifiuta spariscono, e qui blocchiamo anche la modifica dei
  // campi) — a differenza di un ordine rifiutato, che resta modificabile
  // (finestra di revisione ancora aperta lato Core).
  const locked = job?.status === 'approved'

  const [clientName, setClientName] = useState(job?.client_name || '')
  const [clientCode, setClientCode] = useState(job?.client_code || '')
  const [orderDate, setOrderDate] = useState(toDateInputValue(job?.timestamp))
  const [destination, setDestination] = useState(computeDefaultDestination(job))
  // Codice della destinazione ALTERNATIVA scelta (dd_coddest) — vuoto se non
  // se ne è scelta una reale (es. il default è la sede legale del cliente, o
  // testo libero digitato a mano): mai un dd_coddest per la sede legale, non
  // ne ha uno. Vedi tb_coddest in jenia-core (main.py, /api/jobs/:id/decision).
  const [destinationCode, setDestinationCode] = useState('')
  const [orderType, setOrderType] = useState(computeDefaultOrderType(job))
  const [lines, setLines] = useState(job?.lines || [])
  // Cliente/destinazione non hanno un bottone "Conferma" come le righe — per
  // sapere se il commerciale li ha davvero cambiati (statistiche di
  // accuratezza in jenia-core, vedi order_stats_recorded) serve un flag
  // esplicito impostato SOLO da un'azione utente reale (selectClient/
  // selectDestination o digitazione libera), non dal valore del campo di per
  // sé — quel valore parte già precompilato col match del Queuer, quindi non
  // basta guardare se è vuoto per capire se e' stato toccato (bug corretto
  // 2026-08-13: prima ogni ordine risultava "con intervento manuale" anche a
  // campi intonsi).
  const [clientTouched, setClientTouched] = useState(false)
  const [destinationTouched, setDestinationTouched] = useState(false)
  // Indice della riga sotto il puntatore — solo per mostrare/nascondere il
  // bottone "Conferma" (vedi s.confirmLineBtn più sotto), separato da
  // onLineHover (quello serve alla cerchiatura nel documento a sinistra, non
  // deve dipendere dalla visibilità del bottone qui).
  const [hoveredLineIdx, setHoveredLineIdx] = useState(null)

  // Se cambia l'ordine selezionato, risincronizza lo stato locale
  useEffect(() => {
    setClientName(job?.client_name || '')
    setClientCode(job?.client_code || '')
    setOrderDate(toDateInputValue(job?.timestamp))
    setDestination(computeDefaultDestination(job))
    setDestinationCode('')
    setOrderType(computeDefaultOrderType(job))
    setLines(job?.lines || [])
    setClientTouched(false)
    setDestinationTouched(false)
  }, [job?.job_id])

  // Testi (raw_text/description) delle righe attualmente confermate — il
  // documento a sinistra ne disegna un riquadro VERDE fisso per ciascuna,
  // uno per articolo confermato (N in tutto), indipendente dall'hover.
  // Richiesta esplicita 2026-08-12: "devono esserci N tendine in base al
  // numero di articoli... l'azzurra che scorre [con l'hover] e la verde che
  // appare quando un articolo è confermato" — non più una singola riga
  // "fissata" che l'hover altrove sovrascriveva (approccio precedente,
  // causava il bug segnalato: confermare una riga e poi muovere il mouse
  // altrove la faceva sparire). Ricalcolato ad ogni cambio di `lines`, non
  // solo alla conferma: sconfermare una riga (bottone "Modifica" più sotto)
  // fa sparire subito il suo riquadro verde.
  useEffect(() => {
    onConfirmedLinesChange?.(
      lines.filter((l) => l.confirmed).map((l) => l.raw_text || l.description).filter(Boolean)
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines])

  // clientCode/destinationCode/lines vivono solo qui dentro — senza questo,
  // Dashboard.jsx/Coda.jsx (chi manda davvero la POST di decisione) non ha
  // modo di sapere cosa il commerciale ha scelto a mano con la ricerca, e la
  // scelta manuale non arriva mai a Core come override (bug reale
  // 2026-08-07: cliente scelto a mano ma "cliente non trovato in
  // anagrafica" comunque bloccava l'approvazione).
  useEffect(() => {
    onOverridesChange?.({
      client_code: clientCode || null,
      // client_name/destination_name: testo da mostrare, non solo il codice
      // — servono a backend-consumer per persistere nel job cio' che il
      // commerciale ha davvero scelto/confermato (bug corretto 2026-08-13:
      // prima riaprendo un ordine gia' approvato si rivedeva il match
      // originale del Queuer, non la correzione fatta a schermo — la
      // SCRITTURA su Interscambio era comunque giusta, solo la vista dopo
      // era rimasta indietro).
      client_name: clientName || null,
      client_touched: clientTouched,
      destination_code: destinationCode || null,
      destination_name: destination || null,
      destination_touched: destinationTouched,
      // confirmed: per le statistiche di accuratezza in jenia-core
      // (order_stats_recorded) — l'unico segnale esplicito di "il
      // commerciale ha guardato questa riga ed era corretta così com'era",
      // a differenza di matched_article che e' sempre valorizzato (parte
      // gia' precompilato dal match del Queuer) e quindi non basta da solo
      // a distinguere una riga toccata da una lasciata intonsa.
      lines: lines.map((l) => ({
        matched_article: l.matched_article || null,
        description: l.description || null,
        qty: l.qty,
        confirmed: !!l.confirmed,
      })),
    })
  }, [clientName, clientCode, clientTouched, destination, destinationCode, destinationTouched, lines, onOverridesChange])

  // Best match proposti per Indirizzo: la sede legale del cliente è sempre
  // tra questi (fallback quando l'LLM non ha trovato una destinazione
  // migliore — vedi computeDefaultDestination), più le eventuali sedi note
  // (destination_options). Il valore attuale resta sempre selezionabile
  // anche se non compare tra queste (es. destinazione grezza estratta
  // dall'LLM, non ancora in anagrafica).
  const knownDestinations = [
    ...(job?.client_legal_address ? [{ name: job.client_legal_address }] : []),
    ...(job?.destination_options || []),
  ]
  const knownDestinationNames = knownDestinations.map((o) => o.name)
  const destinationBestMatches = destination && !knownDestinationNames.includes(destination)
    ? [{ name: destination }, ...knownDestinations]
    : knownDestinations

  // Selezionare un'alternativa deve aggiornare insieme nome E codice
  // gestionale (an_conto) — sono lo stesso record di anagrafica, non due
  // scelte indipendenti.
  const selectClient = (match) => {
    setClientName(match.name)
    setClientCode(match.code || '')
    setClientTouched(true)
  }

  // Stesso principio di selectClient, ma qui il codice può legittimamente
  // restare vuoto (sede legale o testo libero non hanno un dd_coddest reale
  // — vedi commento su destinationCode più sopra).
  const selectDestination = (match) => {
    setDestination(match.name)
    setDestinationCode(match.code || '')
    setDestinationTouched(true)
  }

  const updateLine = (idx, key, value) => {
    setLines((prev) => prev.map((line, i) => (i === idx ? { ...line, [key]: value } : line)))
  }

  // Selezionare una descrizione alternativa deve aggiornare insieme
  // descrizione E codice articolo (matched_article) — stesso record del
  // Matcher, non due campi indipendenti (stesso principio di selectClient).
  const selectLineDescription = (idx, opt) => {
    setLines((prev) => prev.map((line, i) =>
      i === idx ? { ...line, description: opt.name, matched_article: opt.code || line.matched_article } : line
    ))
  }

  const addLine = () => {
    setLines((prev) => [...prev, { description: '', raw_text: '', matched_article: '', qty: '', um: 'PZ', delivery: '' }])
  }

  const removeLine = (idx) => {
    setLines((prev) => prev.filter((_, i) => i !== idx))
  }

  // Bottone "Conferma riga" (in fondo a ogni riga, a piena larghezza): la
  // prima volta che si clicca su una riga non confermata, la segna come
  // confermata (colora la riga di verde, e il documento a sinistra le
  // disegna un riquadro verde fisso — vedi l'effect su onConfirmedLinesChange
  // più sopra) e passa alla successiva. Se la riga è già confermata, il
  // click la sconferma (toglie il verde, in entrambi i pannelli) senza
  // avanzare — così un click per errore si annulla facilmente, non è "a
  // senso unico".
  const toggleLineConfirmed = (idx) => {
    setLines((prev) => prev.map((line, i) => (i === idx ? { ...line, confirmed: !line.confirmed } : line)))
  }

  const clientMatches = job?.client_matches || []

  // Indice della prima riga non confermata: solo QUESTA riga mostra il
  // bottone "Conferma" (sempre visibile, non più legato all'hover — il
  // continuo apparire/scomparire dava fastidio). Non si può "saltare avanti"
  // a righe successive senza prima confermare questa, quindi mostrarne una
  // sola alla volta è coerente col flusso. -1 se sono tutte confermate.
  const firstUnconfirmedIdx = lines.findIndex((l) => !l.confirmed)

  // L'evidenziazione nel documento segue SOLO l'hover reale (richiesta
  // esplicita 2026-08-12) — non c'è più una riga "attiva" di default che si
  // rispecchia da sola all'apertura dell'ordine o dopo una conferma. Effetto
  // collaterale accettato consapevolmente: prima questo stesso meccanismo
  // faceva anche scorrere automaticamente il documento sulla riga attiva
  // (onLineActivate), perché PdfViewer calcola DOVE scorrere a partire dal
  // riquadro evidenziato — tolto l'hover automatico, sparisce anche quello
  // scroll automatico. Il doppio click su una riga (onDoubleClick più sotto)
  // resta il modo per scorrere lì manualmente.

  // Il banner sopra "Recap. ordine" mostra sia gli errori sollevati dai
  // fratelli (PdfViewer/Dashboard.jsx via systemErrors) sia l'eventuale
  // errore già presente nel job stesso (system_error, es. matching andato
  // in errore lato Queuer) — vedi TODO in CLAUDE.md.
  const allSystemErrors = [
    ...(job?.system_error ? [job.system_error] : []),
    ...systemErrors,
  ]
  // Il popup si richiude cliccando "Ho capito"/fuori, ma deve ririapparire
  // se arriva un errore DIVERSO da quello appena chiuso (es. click su
  // "Conferma" due volte, due 422 diversi) — confrontiamo il contenuto, non
  // solo "c'e' un errore": dismissedKey resta quello vecchio finche' il set
  // di errori non cambia davvero.
  const errorsKey = allSystemErrors.map((e) => `${e.code ?? ''}:${e.message}`).join('|')
  const [dismissedErrorsKey, setDismissedErrorsKey] = useState('')
  const showErrorModal = errorsKey !== '' && errorsKey !== dismissedErrorsKey

  return (
    <div style={s.container}>
      {showErrorModal && (
        <ErrorModal errors={allSystemErrors} onDismiss={() => setDismissedErrorsKey(errorsKey)} />
      )}

      {/* Recap. ordine e Righe ordine sono un unico contenitore continuo
          (non due card separate) proprio per permettere al campo Cliente,
          reso "bloccato" più sotto, di restare fisso per tutta la durata
          dello scroll — sticky resta ancorato solo entro i confini del
          proprio contenitore, quindi se Recap. ordine fosse ancora una
          card a sé (corta), l'aggancio si scollerebbe ben prima della fine
          di Righe ordine. Le due sezioni restano visivamente distinte
          tramite s.sectionDivider invece che due bordi/ombre separati. */}
      <div style={s.mergedCard}>
        <div style={s.cardHeader}>
          <Building2 size={15} color="var(--text-2)" />
          <span style={s.cardTitle}>Riepilogo Ordine</span>
          {job?.client_name && <span style={s.livePill}>estratto live</span>}
        </div>

        {/* Campo Cliente "bloccato": come bloccare una riga in Excel, resta
            visibile in cima al pannello scrollabile (rightPanel in
            Dashboard.jsx / Coda.jsx) mentre il resto scorre sotto —
            compreso tutto Righe ordine, dato che ora è nello stesso
            contenitore. Sfondo pieno + ombra perché una volta agganciato
            in alto il contenuto sotto continua a scorrere "dietro" di lui. */}
        <div style={s.stickyClientField}>
          <label style={s.label}>Cliente</label>
          <SearchableMatchSelect
            value={clientName}
            valueSuffix={clientCode}
            bestMatches={clientMatches}
            searchEndpoint="/api/clients/search"
            onChange={(name, match) => { if (match) selectClient(match); else { setClientName(name); setClientTouched(true) } }}
            emptyLabel="Non identificato"
            searchPlaceholder="Cerca ragione sociale..."
            invalid={!clientCode}
            disabled={locked}
          />
        </div>

        <div style={s.field}>
          <label style={s.label}>Indirizzo</label>
          <SearchableMatchSelect
            value={destination}
            bestMatches={destinationBestMatches}
            searchEndpoint="/api/destinations/search"
            searchParams={{ conto: clientCode }}
            onChange={(name, match) => {
              if (match) selectDestination(match)
              else { setDestination(name); setDestinationCode(''); setDestinationTouched(true) }
            }}
            emptyLabel="Nessun indirizzo indicato"
            searchPlaceholder="Cerca indirizzo..."
            allowClear
            disabled={locked}
          />
        </div>

        <div style={s.field}>
          <label style={s.label}>Tipo documento</label>
          <LabeledSelect
            value={orderType}
            options={ORDER_TYPE_OPTIONS}
            onChange={setOrderType}
            emptyLabel="Ordine"
            disabled={locked}
          />
        </div>

        <div style={s.sectionDivider} />

        <div style={s.cardHeader}>
          <Package size={15} color="var(--text-2)" />
          <span style={s.cardTitle}>Righe ordine</span>
          <span style={s.countPill}>{lines.length} righe estratte</span>
          {!locked && (
            <button type="button" style={s.addLineBtn} onClick={addLine}>
              <Plus size={13} />
              Aggiungi riga
            </button>
          )}
        </div>

        <div style={s.linesTable}>
          {lines.map((line, idx) => (
            <div
              key={idx}
              style={{
                ...s.lineCard,
                ...(line.confirmed ? s.lineCardConfirmed : {}),
                ...(idx === firstUnconfirmedIdx ? s.lineCardActive : {}),
              }}
              onMouseEnter={() => { onLineHover?.(line); setHoveredLineIdx(idx) }}
              onMouseLeave={() => { onLineHover?.(null); setHoveredLineIdx(null) }}
              onDoubleClick={() => onLineActivate?.(line)}
            >
              {/* Testo originale: esattamente quello letto dal documento
                  (PDF/txt), prima e indipendentemente dal match — non una
                  tendina (non c'è nulla da "scegliere" qui, è la fonte),
                  solo un campo di testo modificabile per correggere errori
                  di estrazione/OCR. Stile tratteggiato + corsivo apposta per
                  distinguerlo dal Titolo (il match) qui sotto. Label sopra
                  su richiesta, per chiarire che questo testo non è scritto a
                  mano ma estratto dall'agent (LLM) — stesso rischio di
                  errore di qualunque estrazione automatica, da qui il campo
                  editabile per correggerlo. */}
              <div style={s.lineExtractedLabel}>Testo Estratto</div>
              <div style={s.lineCardHeader}>
                <span style={s.lineNumber}>{idx + 1}</span>
                <input
                  type="text"
                  value={line.raw_text || ''}
                  onChange={(e) => updateLine(idx, 'raw_text', e.target.value)}
                  placeholder="Testo estratto dal documento originale"
                  title="Testo originale estratto dal documento — non è il match, solo ciò che c'è scritto nel PDF/file"
                  disabled={locked || !!line.confirmed}
                  style={{ ...s.rawTextInput, ...(locked || line.confirmed ? s.disabledField : {}) }}
                />
                {!locked && (
                  <button
                    type="button"
                    style={s.removeLineBtn}
                    onClick={() => removeLine(idx)}
                    aria-label="Rimuovi riga"
                    title="Rimuovi riga"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>

              {/* Articolo su una riga tutta sua, Cod. art./Q.tà/UM insieme
                  sulla riga sotto — SEMPRE, anche su monitor grande (non solo
                  sotto una soglia stretta come in un primo giro: pure su
                  schermi grandi capita che il nome articolo sia troppo lungo
                  per stare su una riga a 4 colonne e venga tagliato, quindi
                  niente più variante desktop a griglia). */}
              <div style={s.lineCardBodyMobile}>
                <div style={s.lineFieldRow}>
                  <div style={s.lineField}>
                    <label style={s.lineFieldLabel}>Articolo</label>
                    <LineDescriptionSelect
                      value={line.description || ''}
                      options={line.description_matches || []}
                      onChange={(opt) => selectLineDescription(idx, opt)}
                      invalid={!line.matched_article}
                      disabled={locked || !!line.confirmed}
                    />
                  </div>
                </div>
                <div style={s.lineFieldRow}>
                  <div style={s.lineField}>
                    <label style={s.lineFieldLabel}>Cod. art.</label>
                    {/* Sola visualizzazione, non editabile a mano — per cambiare
                        articolo (e quindi il codice) si usa la ricerca nel
                        campo "Articolo" qui sopra (cerca per nome E per codice,
                        vedi selectLineDescription), stesso principio del
                        codice cliente accanto a CLIENTE. */}
                    <div style={s.lineFieldReadonly}>{line.matched_article || '—'}</div>
                  </div>
                  <div style={s.lineField}>
                    <label style={s.lineFieldLabel}>Q.tà</label>
                    <input
                      type="text"
                      value={line.qty}
                      onChange={(e) => updateLine(idx, 'qty', e.target.value)}
                      disabled={locked || !!line.confirmed}
                      style={{ ...s.lineFieldInput, ...(locked || line.confirmed ? s.disabledField : {}) }}
                    />
                  </div>
                  <div style={s.lineField}>
                    <label style={s.lineFieldLabel}>UM</label>
                    <UmSelect value={line.um || 'NR'} onChange={(opt) => updateLine(idx, 'um', opt)} disabled={locked || !!line.confirmed} />
                  </div>
                </div>
                {/* Data di consegna per riga: non serve per questo progetto,
                    nascosta su richiesta. line.delivery resta nel dato e in
                    updateLine (non rimosso) nel caso torni utile in futuro. */}
              </div>

              {/* Conferma riga — a piena larghezza, sotto Articolo/Cod.
                  art./Q.tà/UM. Sempre visibile SOLO sulla prima riga non
                  confermata (niente più apparire/scomparire in hover, dava
                  fastidio): non si può confermare una riga successiva senza
                  aver prima confermato questa, quindi ha senso mostrarne una
                  sola alla volta. Clic: colora la riga di verde e passa alla
                  successiva (stesso effetto di un doppio click su quella
                  riga, vedi toggleLineConfirmed). */}
              {!locked && idx === firstUnconfirmedIdx && (
                <button
                  type="button"
                  style={s.confirmLineBtn}
                  onClick={() => toggleLineConfirmed(idx)}
                >
                  <Check size={14} />
                  Conferma
                </button>
              )}

              {/* Righe già confermate: nessun bottone fisso (sarebbe fuori
                  posto, non sono più "l'azione corrente"), ma un piccolo
                  "Modifica" rivelato in hover per poterle sconfermare e
                  tornare a editarle — l'unico modo rimasto di sbloccare una
                  riga passata, dato che il bottone Conferma non ci passa
                  più. */}
              {!locked && line.confirmed && hoveredLineIdx === idx && (
                <button
                  type="button"
                  style={s.editConfirmedLineBtn}
                  onClick={() => toggleLineConfirmed(idx)}
                >
                  Modifica
                </button>
              )}
            </div>
          ))}

          {lines.length === 0 && (
            <div style={s.linesEmpty}>Nessuna riga estratta.</div>
          )}
        </div>
      </div>
    </div>
  )
}

const s = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    flex: 1,
  },
  // Codice errore nel popup centrale — vedi ErrorModal e
  // `systemErrors`/`job.system_error` in OrderReview.
  errorBannerCode: {
    flexShrink: 0,
    fontSize: 10.5,
    fontWeight: 700,
    fontFamily: 'var(--font-mono)',
    padding: '2px 8px',
    borderRadius: 999,
    background: 'rgba(168,73,76,0.14)',
    color: 'var(--danger)',
    whiteSpace: 'nowrap',
  },
  errorModalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15,23,42,0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: 20,
  },
  errorModalCard: {
    background: '#ffffff',
    borderRadius: 14,
    padding: '22px 24px',
    maxWidth: 440,
    width: '100%',
    boxShadow: '0 24px 60px -20px rgba(15,23,42,0.35)',
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  errorModalRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
  },
  errorModalText: {
    flex: 1,
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1.5,
    color: 'var(--danger)',
  },
  errorModalClose: {
    alignSelf: 'flex-end',
    padding: '8px 16px',
    borderRadius: 8,
    border: 'none',
    background: 'var(--text-1)',
    color: '#ffffff',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
  // Stesso layout di s.field (label + controllo, gap 4), stessa assenza di
  // bordo/sfondo/ombra visibili — allineato a Indirizzo/Tipo documento su
  // richiesta (2026-08-12): niente più "card" a sé, solo il campo. Resta
  // `position: sticky` (agganciato in cima al pannello scrollabile, sopra a
  // ciò che scorre sotto) — `background: '#FFFFFF'` è necessario per
  // coprire davvero il contenuto che gli scorre dietro (stesso bianco del
  // contenitore, quindi invisibile come "box" pur restando opaco), non è
  // decorativo.
  stickyClientField: {
    position: 'sticky',
    top: 0,
    zIndex: 20,
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    background: '#FFFFFF',
  },
  // Recap. ordine e Righe ordine sono sezioni dello stesso contenitore (non
  // due card separate) — vedi commento nel JSX sul perché (sticky del
  // campo Cliente deve poter restare ancorato per tutto lo scroll).
  mergedCard: {
    background: '#FFFFFF',
    border: '1px solid var(--hairline)',
    borderRadius: 12,
    padding: 14,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  sectionDivider: {
    height: 1,
    background: 'var(--hairline)',
    margin: '2px 0',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 2,
  },
  cardTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--text)',
  },
  livePill: {
    marginLeft: 'auto',
    fontSize: 10,
    fontWeight: 600,
    color: 'var(--jenia-violet)',
    background: 'var(--grad-jenia-soft)',
    padding: '3px 8px',
    borderRadius: 999,
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  // Segnala "nessun match trovato" (Cliente non identificato, riga senza
  // descrizione) — sovrascrive solo bordo/sfondo dello stile base del
  // campo, il resto (padding, font, ecc.) resta quello del trigger/input
  // originale su cui viene mischiato con lo spread `...s.invalidField`.
  invalidField: {
    border: '1.5px solid var(--danger)',
    background: 'var(--danger-soft-bg)',
  },
  // Riga confermata (line.confirmed): tutti i campi editabili diventano
  // bloccati finché non si sconferma la riga (bottone Conferma, vedi
  // toggleLineConfirmed) — sovrascrive solo cursore/opacità, il colore di
  // sfondo resta quello che il campo aveva già (la card confermata,
  // s.lineCardConfirmed, ha solo il bordo verde, niente sfondo).
  disabledField: {
    opacity: 0.65,
    cursor: 'not-allowed',
  },
  label: {
    fontSize: 10.5,
    fontWeight: 600,
    color: 'var(--text-3)',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  selectLike: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: '8px 10px',
    border: '1px solid var(--hairline)',
    borderRadius: 8,
    background: 'var(--bg)',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  selectText: {
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--text)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  selectTrailing: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
    marginLeft: 8,
  },
  selectSuffix: {
    fontSize: 12.5,
    fontWeight: 500,
    color: 'var(--text-3)',
    whiteSpace: 'nowrap',
  },
  clientDropdown: {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    right: 0,
    zIndex: 30,
    background: '#FFFFFF',
    border: '1px solid var(--hairline)',
    borderRadius: 10,
    boxShadow: '0 8px 24px rgba(10,10,10,0.12)',
    padding: 4,
    maxHeight: 220,
    overflow: 'auto',
  },
  clientDropdownItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    padding: '8px 10px',
    border: 'none',
    background: 'transparent',
    borderRadius: 8,
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: 'inherit',
  },
  clientDropdownCheck: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 14,
    flexShrink: 0,
    color: 'var(--jenia-violet)',
  },
  clientDropdownName: {
    flex: 1,
    minWidth: 0,
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--text)',
    // A capo invece di troncare con "..." (richiesta esplicita 2026-08-14:
    // nomi/indirizzi/descrizioni lunghi venivano tagliati) — nessun testo va
    // mai perso, indipendentemente da quanto è lungo o da quale tendina lo
    // mostra (questo stile è condiviso da SearchableMatchSelect e
    // LabeledSelect).
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  },
  clientDropdownEmpty: {
    padding: '10px 12px',
    fontSize: 12.5,
    color: 'var(--text-3)',
    textAlign: 'center',
  },
  searchSelectDropdown: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    maxHeight: 420,
    background: '#FFFFFF',
    border: '1px solid var(--hairline)',
    borderRadius: 10,
    boxShadow: '0 8px 24px rgba(10,10,10,0.12)',
    padding: 4,
    // 'auto' (non più 'hidden'): se lo spazio disponibile calcolato in
    // useAnchoredMenu è più stretto di quanto servirebbe alle sezioni interne
    // (best match + ricerca), il contenitore stesso scrolla invece di
    // tagliare via silenziosamente la parte in eccesso.
    overflow: 'auto',
  },
  searchSelectMatches: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    maxHeight: 170,
    overflow: 'auto',
    flexShrink: 0,
  },
  splitColumnHeader: {
    padding: '6px 10px 4px',
    fontSize: 10.5,
    fontWeight: 700,
    color: 'var(--text-3)',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  searchSelectDivider: {
    height: 1,
    background: 'var(--hairline)',
    margin: '2px 4px',
    flexShrink: 0,
  },
  searchSelectSearchRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '2px 4px 4px',
    flexShrink: 0,
  },
  searchSelectInput: {
    flex: 1,
    minWidth: 0,
    border: '1px solid var(--hairline-strong)',
    borderRadius: 8,
    padding: '7px 10px',
    fontSize: 13,
    fontFamily: 'inherit',
    color: 'var(--text)',
    background: 'var(--bg)',
    outline: 'none',
  },
  searchSelectSearchBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    flexShrink: 0,
    border: '1px solid var(--hairline-strong)',
    borderRadius: 8,
    background: 'var(--bg)',
    color: 'var(--text-2)',
    cursor: 'pointer',
  },
  searchResultsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    overflow: 'auto',
    maxHeight: 220,
    padding: '2px 0 4px',
  },
  countPill: {
    marginLeft: 'auto',
    fontSize: 11,
    fontWeight: 500,
    color: 'var(--text-2)',
    background: 'var(--bg-subtle)',
    border: '1px solid var(--hairline)',
    padding: '3px 10px',
    borderRadius: 999,
  },
  addLineBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    padding: '4px 10px',
    fontSize: 11.5,
    fontWeight: 500,
    fontFamily: 'inherit',
    color: 'var(--text-2)',
    background: '#FFFFFF',
    border: '1px solid var(--hairline-strong)',
    borderRadius: 999,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  linesTable: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  lineCard: {
    border: '1px solid var(--hairline)',
    borderRadius: 10,
    background: '#FFFFFF',
  },
  // Riga confermata (bottone Conferma riga cliccato) — solo il bordo si
  // colora di verde, niente sfondo/sfumatura interna (stesso trattamento di
  // lineCardActive qui sotto, tolta su richiesta). Usa `--ok` (verde pieno)
  // invece di `--ok-border` (troppo trasparente, quasi invisibile) e 2px
  // invece di 1.5px — su richiesta, doveva essere più leggibile scorrendo
  // la lista.
  lineCardConfirmed: {
    border: '2px solid var(--ok)',
  },
  // Riga "attiva" (la prima non confermata, stessa condizione che mostra il
  // bottone Conferma, vedi firstUnconfirmedIdx) — solo il bordo si colora di
  // azzurro, niente sfondo/sfumatura interna (tolta su richiesta). `--jenia-
  // violet` nonostante il nome è in realtà un azzurro (vedi Design system
  // in CLAUDE.md).
  lineCardActive: {
    border: '1.5px solid var(--jenia-violet)',
  },
  // Label "Testo estratto dall'agent" sopra lineCardHeader (stesso sfondo,
  // nessuna cucitura visibile) — è lei ora ad essere flush in alto nella
  // card, quindi l'angolo arrotondato passa a lei (vedi lineCardHeader
  // sotto, che l'ha perso).
  lineExtractedLabel: {
    padding: '6px 12px 0',
    fontSize: 10.5,
    fontWeight: 600,
    color: 'var(--text-3)',
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
    background: 'var(--bg-subtle)',
    borderTopLeftRadius: 9,
    borderTopRightRadius: 9,
  },
  lineCardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '4px 12px 8px',
    background: 'var(--bg-subtle)',
    borderBottom: '1px solid var(--hairline)',
  },
  lineNumber: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 22,
    height: 22,
    borderRadius: 6,
    background: 'var(--hairline)',
    color: 'var(--text-2)',
    fontSize: 11.5,
    fontWeight: 600,
    flexShrink: 0,
  },
  // Testo originale (estratto dal documento, non il match) nell'header della
  // riga — bordo tratteggiato + corsivo + colore più tenue apposta, per
  // distinguerlo a colpo d'occhio dal Titolo (il match, sotto in
  // lineFieldInput/lineDescSelectLike, bordo continuo e testo pieno).
  rawTextInput: {
    flex: 1,
    minWidth: 0,
    border: '1px dashed var(--hairline-strong)',
    borderRadius: 8,
    background: 'var(--bg)',
    color: 'var(--text-2)',
    fontStyle: 'italic',
    fontSize: 13,
    fontFamily: 'inherit',
    padding: '7px 10px',
  },
  lineDescWrap: {
    position: 'relative',
    flex: 1,
    minWidth: 0,
  },
  lineDescSelectLike: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    width: '100%',
    border: '1px solid var(--hairline-strong)',
    borderRadius: 8,
    background: 'var(--bg)',
    cursor: 'pointer',
    fontFamily: 'inherit',
    padding: '7px 10px',
  },
  lineDescSelectText: {
    fontSize: 13.5,
    fontWeight: 500,
    color: 'var(--text)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
  },
  removeLineBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    flexShrink: 0,
    border: 'none',
    background: 'transparent',
    color: 'var(--text-3)',
    borderRadius: 6,
    cursor: 'pointer',
  },
  // Articolo su una riga tutta sua, Cod. art./Q.tà/UM insieme sulla riga
  // sotto (vedi lineFieldRow) — SEMPRE, non solo su schermi stretti: anche
  // su monitor grande un nome articolo lungo veniva tagliato dalla vecchia
  // griglia a 4 colonne (rimossa, teneva Articolo/Cod. art./Q.tà/UM tutti
  // sulla stessa riga).
  lineCardBodyMobile: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    padding: '12px 14px',
  },
  // `minWidth: 0` qui è ciò che fa davvero funzionare il troncamento con
  // ellissi sull'Articolo (vedi lineDescSelectText) — senza, questo div,
  // essendo lui stesso un flex item di lineCardBodyMobile, di default si
  // rifiuta di restringersi sotto la larghezza "naturale" del contenuto
  // (min-width: auto), quindi Articolo non si accorcia mai davvero e va a
  // capo/eccede invece di troncare su schermi stretti.
  lineFieldRow: {
    display: 'flex',
    gap: 14,
    minWidth: 0,
  },
  lineField: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    minWidth: 0,
    flex: 1,
  },
  lineFieldLabel: {
    fontSize: 10.5,
    fontWeight: 600,
    color: 'var(--text-3)',
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
  },
  lineFieldInput: {
    width: '100%',
    padding: '7px 9px',
    fontSize: 13,
    border: '1px solid var(--hairline)',
    borderRadius: 8,
    background: 'var(--bg)',
    color: 'var(--text)',
    fontFamily: 'inherit',
  },
  lineFieldReadonly: {
    width: '100%',
    padding: '7px 9px',
    fontSize: 13,
    border: '1px solid var(--hairline)',
    borderRadius: 8,
    background: 'var(--bg-subtle)',
    color: 'var(--text-2)',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
  umSelectLike: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: '7px 9px',
    border: '1px solid var(--hairline)',
    borderRadius: 8,
    background: 'var(--bg)',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  umSelectText: {
    fontSize: 13,
    color: 'var(--text)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  umDropdown: {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    right: 0,
    zIndex: 30,
    background: '#FFFFFF',
    border: '1px solid var(--hairline)',
    borderRadius: 10,
    boxShadow: '0 8px 24px rgba(10,10,10,0.12)',
    padding: 4,
    maxHeight: 180,
    overflow: 'auto',
  },
  umDropdownItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    padding: '7px 9px',
    border: 'none',
    background: 'transparent',
    borderRadius: 8,
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: 'inherit',
  },
  umDropdownLabel: {
    fontSize: 13,
    color: 'var(--text)',
  },
  linesEmpty: {
    padding: '20px 8px',
    textAlign: 'center',
    color: 'var(--text-3)',
    fontSize: 12.5,
  },
  // Bottone "Conferma riga", a piena larghezza sotto Articolo/Cod. art./
  // Q.tà/UM — mostrato solo sulla prima riga non confermata (sempre
  // visibile lì, non più legato all'hover). Sfondo trasparente apposta: il
  // colore/sfumatura arriva dalla card (s.lineCardActive), non da qui — così
  // non c'è uno "stacco" visibile al confine tra corpo riga e bottone.
  confirmLineBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    width: '100%',
    padding: '9px 12px',
    fontSize: 12.5,
    fontWeight: 600,
    fontFamily: 'inherit',
    color: 'var(--jenia-violet)',
    background: 'transparent',
    border: 'none',
    borderRadius: '0 0 9px 9px',
    cursor: 'pointer',
  },
  // Piccolo bottone "Sconferma e modifica" per righe già confermate — non
  // fisso come s.confirmLineBtn, rivelato solo in hover (qui l'apparire/
  // scomparire non dà fastidio perché non è più l'azione da fare ora, è
  // un'eccezione occasionale). Stile deliberatamente più discreto/testuale.
  editConfirmedLineBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    padding: '7px 12px',
    fontSize: 11.5,
    fontWeight: 500,
    fontFamily: 'inherit',
    color: 'var(--ok)',
    background: 'transparent',
    border: 'none',
    borderTop: '1px dashed var(--ok-border)',
    borderRadius: '0 0 9px 9px',
    cursor: 'pointer',
  },
}
